import type { ChatMessage } from './types';

// Folds one message into a transcript. Shared by the tab that owns the session and
// by the popped-out window so the two can never disagree about what the transcript
// contains — both run this same reducer over the same sequence of messages.
//
// Two things have to be absorbed here:
//
//   * Connect redelivers transcript items after a reconnect, so the same server
//     item id can arrive more than once.
//   * Connect also delivers the customer's own message back to the customer
//     participant. The UI echoes that message locally the moment it is sent so
//     typing feels responsive, which means every outbound message arrives twice:
//     once as a local echo, once from the server. The echo carries a synthetic id,
//     so an id check alone does not catch it (WIPE-985 follow-up).
export function mergeMessage(previous: ChatMessage[], message: ChatMessage): ChatMessage[] {
  if (previous.some((m) => m.id === message.id)) return previous;

  // The server's copy of a message this window already echoed: adopt the server's
  // id and instant in place rather than appending a second copy. Matching on
  // content is what is available — the echo is created before the send, so it has
  // no server id to correlate on.
  if (message.kind === 'customer') {
    const pendingIndex = previous.findIndex((m) => m.pending && m.content === message.content);
    if (pendingIndex !== -1) {
      const next = [...previous];
      next[pendingIndex] = message;
      return next;
    }
  }

  return [...previous, message];
}
